import { useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box, Typography, Button, CircularProgress,
    Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails,
    Chip, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, Paper, Divider, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BuildIcon from '@mui/icons-material/Build';
import { ToggleButton, ToggleButtonGroup } from '@mui/lab';
import axios from 'axios';
import urls from '../../Mobx/Urls';
import Layout from '../Layout/Layout';

// ---------------------------------------------------------------------------
// Test definitions
// ---------------------------------------------------------------------------

const ENG_TO_HEB = 'dictionary_eng_to_heb';
const HEB_TO_ENG = 'dictionary_heb_to_eng';

const TESTS = {
    [ENG_TO_HEB]: [
        {
            id: 'eng_test1_trns_hebrew',
            title: 'בדיקה 1 – מסיחים עבור תרגום',
            checks: [
                'כל המסיחים הם בעברית',
                'אין בתוך המסיחים את מילת התרגום המקורית',
                'אין מילים כפולות בתוך המסיחים',
                'אין בין המסיחים תרגום נכון אחר של אותה מילה (תרגום שהוא נכון למילה אבל אינו התרגום הספציפי של הערך הזה)',
            ],
        },
        {
            id: 'eng_test2_original_english',
            title: 'בדיקה 2 – מסיחים עבור אוריגינאל',
            checks: [
                'כל המסיחים הם באנגלית',
                'אין בתוך המסיחים את מילת האוריגינאל המקורית',
                'אין מילים כפולות בתוך המסיחים',
            ],
        },
        {
            id: 'eng_test3_sentences_english',
            title: 'בדיקה 3 – משפטים והגדרות באנגלית',
            checks: [
                'כל המשפטים וההגדרות הם באנגלית',
            ],
        },
        {
            id: 'eng_test4_word_families_english',
            title: 'בדיקה 4 – מסיחים עבור משפחות מילים',
            checks: [
                'כל המסיחים הם באנגלית',
                'אין בתוך המסיחים את מילת האוריגינאל המקורית',
                'אין מילים כפולות בתוך המסיחים',
            ],
        },
        {
            id: 'eng_test5_sentences_missing_word',
            title: 'בדיקה 5 – משפטים שחסר בהם מילה באמצע',
            checks: [
                'כל המשפטים הם באנגלית',
                'יש מילה חסרה בתוך המשפט',
            ],
        },
        {
            id: 'eng_test6_opposites_english',
            title: 'בדיקה 6 – הפכים וזוגות',
            checks: [
                'כל המילים הם באנגלית',
            ],
        },
        {
            id: 'eng_test7_synonyms_english',
            title: 'בדיקה 7 – מילים נרדפות',
            checks: [
                'כל המילים הם באנגלית',
            ],
        },
    ],
    [HEB_TO_ENG]: [
        {
            id: 'heb_test1_trns_english',
            title: 'בדיקה 1 – מסיחים עבור תרגום',
            checks: [
                'כל המסיחים הם באנגלית',
                'אין בתוך המסיחים את מילת התרגום המקורית',
                'אין מילים כפולות בתוך המסיחים',
                'אין בין המסיחים תרגום נכון אחר של אותה מילה (תרגום שהוא נכון למילה אבל אינו התרגום הספציפי של הערך הזה)',
            ],
        },
        {
            id: 'heb_test2_original_hebrew',
            title: 'בדיקה 2 – מסיחים עבור אוריגינאל',
            checks: [
                'כל המסיחים הם בעברית',
                'אין בתוך המסיחים את מילת האוריגינאל המקורית, גם לא עם ניקוד אחר (לפי ה-value ללא ניקוד)',
                'אין מילים כפולות בתוך המסיחים',
            ],
        },
        {
            id: 'heb_test3_sentences_hebrew',
            title: 'בדיקה 3 – משפטים והגדרות בעברית',
            checks: [
                'כל המשפטים וההגדרות הם בעברית',
            ],
        },
        {
            id: 'heb_test4_word_families_hebrew',
            title: 'בדיקה 4 – מסיחים עבור משפחות מילים',
            checks: [
                'כל המסיחים הם בעברית',
                'אין בתוך המסיחים את מילת האוריגינאל המקורית',
                'אין מילים כפולות בתוך המסיחים',
            ],
        },
        {
            id: 'heb_test5_sentences_missing_word',
            title: 'בדיקה 5 – משפטים שחסר בהם מילה באמצע',
            checks: [
                'כל המשפטים הם בעברית',
                'יש מילה חסרה בתוך המשפט',
            ],
        },
        {
            id: 'heb_test6_opposites_english',
            title: 'בדיקה 6 – הפכים וזוגות',
            checks: [
                'כל המילים הם בעברית',
            ],
        },
        {
            id: 'heb_test7_synonyms_english',
            title: 'בדיקה 7 – מילים נרדפות',
            checks: [
                'כל המילים הם בעברית',
            ],
        },
    ],
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TestResultTable({ words }) {
    if (!words || words.length === 0) return null;
    const columns = Object.keys(words[0]);
    return (
        <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map(col => (
                            <TableCell key={col} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                {col}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {words.map((row, i) => (
                        <TableRow key={i} hover>
                            {columns.map(col => (
                                <TableCell key={col} sx={{ direction: 'ltr', textAlign: 'left' }}>
                                    {Array.isArray(row[col])
                                        ? row[col].join(', ')
                                        : String(row[col] ?? '')}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function TestCard({ test, result, isRunning, isFixing, onRun, onFix }) {
    const passed = result && result.count === 0;
    const hasResult = !!result;
    const canFix = hasResult && !passed;

    return (
        <Accordion
            disableGutters
            sx={{
                mb: 1,
                border: hasResult
                    ? passed
                        ? '1px solid #4caf50'
                        : '1px solid #f44336'
                    : '1px solid #e0e0e0',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, flexWrap: 'wrap' }}>
                    {hasResult && (
                        passed
                            ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                            : <ErrorOutlineIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="subtitle1" fontWeight="bold">
                        {test.title}
                    </Typography>
                    {hasResult && (
                        <Chip
                            label={passed ? 'עבר' : `${result.count} בעיות`}
                            color={passed ? 'success' : 'error'}
                            size="small"
                        />
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        {canFix && (
                            <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                onClick={(e) => { e.stopPropagation(); if (window.confirm('האם אתה בטוח שברצונך לתקן?')) onFix(test.id); }}
                                disabled={isFixing || isRunning}
                                startIcon={isFixing ? <CircularProgress size={14} color="inherit" /> : <BuildIcon fontSize="small" />}
                            >
                                {isFixing ? 'מתקן...' : 'תקן'}
                            </Button>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); onRun(test.id); }}
                            disabled={isRunning || isFixing}
                            startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {isRunning ? 'בודק...' : 'הפעל'}
                        </Button>
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{ mb: 1.5 }}>
                    {test.checks.map((c, i) => (
                        <Typography key={i} variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.5 }}>
                            • {c}
                        </Typography>
                    ))}
                </Box>
                {hasResult && !passed && (
                    <>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                            נמצאו {result.count} מילים עם בעיות:
                        </Typography>
                        <TestResultTable words={result.words} />
                    </>
                )}
                {hasResult && passed && (
                    <Typography variant="body2" color="success.main">
                        לא נמצאו בעיות
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TestsPage = observer(() => {
    const [dictionaryType, setDictionaryType] = useState(ENG_TO_HEB);
    const [results, setResults] = useState({});
    const [runningTests, setRunningTests] = useState({});
    const [fixingTests, setFixingTests] = useState({});
    const [isRunningAll, setIsRunningAll] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

    const runTest = useCallback(async (testId) => {
        setRunningTests(prev => ({ ...prev, [testId]: true }));
        try {
            const response = await axios.post(
                urls.tests.runTest,
                { testId, dictionaryType },
                { withCredentials: true }
            );
            setResults(prev => ({ ...prev, [testId]: response.data }));
        } catch (error) {
            setErrorMessage(`שגיאה בהרצת הבדיקה: ${error?.response?.data || error.message}`);
            setIsSnackbarOpen(true);
        } finally {
            setRunningTests(prev => ({ ...prev, [testId]: false }));
        }
    }, [dictionaryType]);

    const fixTest = useCallback(async (testId) => {
        setFixingTests(prev => ({ ...prev, [testId]: true }));
        try {
            await axios.post(
                urls.tests.fixTest,
                { testId, dictionaryType },
                { withCredentials: true }
            );
            // re-run the test after fixing to reflect updated state
            await runTest(testId);
        } catch (error) {
            setErrorMessage(`שגיאה בתיקון: ${error?.response?.data || error.message}`);
            setIsSnackbarOpen(true);
        } finally {
            setFixingTests(prev => ({ ...prev, [testId]: false }));
        }
    }, [dictionaryType, runTest]);

    const runAllTests = useCallback(async () => {
        setIsRunningAll(true);
        const tests = TESTS[dictionaryType];
        for (const test of tests) {
            await runTest(test.id);
        }
        setIsRunningAll(false);
    }, [dictionaryType, runTest]);

    const handleDictionaryTypeChange = (_, newType) => {
        if (newType !== null) {
            setDictionaryType(newType);
            setResults({});
        }
    };

    const currentTests = TESTS[dictionaryType];
    const totalRan = currentTests.filter(t => results[t.id] !== undefined).length;
    const totalFailed = currentTests.filter(t => results[t.id]?.count > 0).length;
    const totalPassed = currentTests.filter(t => results[t.id]?.count === 0).length;

    const tools = [
        <ToggleButtonGroup
            value={dictionaryType}
            exclusive
            onChange={handleDictionaryTypeChange}
            sx={{ maxHeight: 40 }}
        >
            <ToggleButton value={ENG_TO_HEB} sx={{ px: 2, fontWeight: 'bold' }}>
                אנגלי-עברי
            </ToggleButton>
            <ToggleButton value={HEB_TO_ENG} sx={{ px: 2, fontWeight: 'bold' }}>
                עברי-אנגלי
            </ToggleButton>
        </ToggleButtonGroup>,
        <Button
            variant="contained"
            onClick={runAllTests}
            disabled={isRunningAll}
            startIcon={isRunningAll ? <CircularProgress size={16} color="inherit" /> : null}
        >
            {isRunningAll ? 'מריץ את כל הבדיקות...' : 'הרץ את כל הבדיקות'}
        </Button>,
        totalRan > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="עברו"><Chip label={`✓ ${totalPassed}`} color="success" size="small" /></Tooltip>
                <Tooltip title="נכשלו"><Chip label={`✗ ${totalFailed}`} color={totalFailed > 0 ? 'error' : 'default'} size="small" /></Tooltip>
            </Box>
        ),
    ].filter(Boolean);

    return (
        <Layout tools={tools} drawerTools={[]}>
            <Box sx={{ p: 3, maxWidth: 900, mx: 'auto', dir: 'rtl' }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                    בדיקות מילון — {dictionaryType === ENG_TO_HEB ? 'אנגלי עברי' : 'עברי אנגלי'}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {currentTests.map(test => (
                    <TestCard
                        key={test.id}
                        test={test}
                        result={results[test.id]}
                        isRunning={!!runningTests[test.id]}
                        isFixing={!!fixingTests[test.id]}
                        onRun={runTest}
                        onFix={fixTest}
                    />
                ))}
            </Box>

            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={6000}
                onClose={() => setIsSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setIsSnackbarOpen(false)}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Layout>
    );
});

export default TestsPage;
